import { useMemo, useState } from 'react'
import { motion } from 'motion/react'
import { useNewsList } from '@/lib/queries'
import { cn, titleCase } from '@/lib/utils'
import type { NewsCategory } from '@/lib/types'
import { Container } from '@/components/site/primitives'
import { PageHero } from '@/components/site/page-hero'
import { NewsCard } from '@/components/site/cards'
import { Button } from '@/components/ui/button'
import { EmptyState, ErrorState, LoadingBlock } from '@/components/ui/feedback'
import { usePageMeta } from '@/components/layout/site-layout'

const PAGE_SIZE = 9

export default function NewsPage() {
  usePageMeta('News', 'Match reports, announcements and stories from AFC Vikings Cairo.')

  const { data: articles = [], isLoading, isError } = useNewsList()
  const [category, setCategory] = useState<NewsCategory | 'all'>('all')
  const [shown, setShown] = useState(PAGE_SIZE)

  const categories = useMemo(() => {
    const present = new Set(articles.map((article) => article.category).filter(Boolean))
    return ['all', ...present] as (NewsCategory | 'all')[]
  }, [articles])

  const filtered = useMemo(
    () =>
      category === 'all' ? articles : articles.filter((article) => article.category === category),
    [articles, category]
  )

  const [lead, ...rest] = filtered
  const visible = rest.slice(0, shown)

  return (
    <>
      <PageHero
        kicker="Newsroom"
        rail="Latest"
        title="News"
        subtitle="Match reports, club announcements and the stories behind the badge."
      />

      <section className="bg-ink py-12 md:py-20">
        <Container>
          {isError ? (
            <ErrorState title="News did not load" />
          ) : isLoading ? (
            <LoadingBlock label="Loading news" />
          ) : articles.length === 0 ? (
            <EmptyState
              title="Nothing published yet"
              description="Articles written in the club dashboard appear here as soon as they go live."
            />
          ) : (
            <>
              {categories.length > 2 ? (
                <div className="no-scrollbar border-hairline mb-12 flex gap-1 overflow-x-auto border-b">
                  {categories.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => {
                        setCategory(item)
                        setShown(PAGE_SIZE)
                      }}
                      className={cn(
                        'condensed relative shrink-0 px-4 py-3 text-[0.8125rem] font-semibold tracking-[0.16em] transition-colors',
                        category === item ? 'text-blood' : 'text-bone-faint hover:text-bone-dim'
                      )}
                    >
                      {item === 'all' ? 'All' : titleCase(item)}
                      {category === item ? (
                        <motion.span
                          layoutId="news-tab"
                          className="bg-blood absolute inset-x-0 -bottom-px h-0.5"
                        />
                      ) : null}
                    </button>
                  ))}
                </div>
              ) : null}

              {filtered.length === 0 ? (
                <EmptyState title="Nothing in this category yet" />
              ) : (
                <div className="space-y-4">
                  <NewsCard article={lead} featured index={0} />

                  {visible.length > 0 ? (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {visible.map((article, index) => (
                        <NewsCard key={article.id} article={article} index={index + 1} />
                      ))}
                    </div>
                  ) : null}

                  {rest.length > shown ? (
                    <div className="flex justify-center pt-8">
                      <Button variant="outline" onClick={() => setShown((n) => n + PAGE_SIZE)}>
                        Load more ({rest.length - shown} left)
                      </Button>
                    </div>
                  ) : null}
                </div>
              )}
            </>
          )}
        </Container>
      </section>
    </>
  )
}
